import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TimetableResponseDto } from "../../shared/dtos/timetable/TimetableResponseDto";
import { TimetableSummaryDto } from "../../shared/dtos/timetable/TimetableSummaryDto";
import { PaginationDto } from "../../shared/dtos/common/PaginationDto";
import { ServerStatusPayload } from "../../shared/interfaces/ServerStatusPayload";

export interface TimetableState {
  loadingTimetables: boolean;
  loadingTimetable: boolean;
  loadingTimetableUpdate: boolean;
  loadingTimetableDelete: boolean;

  errorLoadingTimetables: string | null;
  errorLoadingTimetable: string | null;
  errorUpdatingTimetable: string | null;
  errorDeletingTimetable: string | null;

  timetables: TimetableSummaryDto[];
  timetable: TimetableResponseDto | null;

  serverStatus: ServerStatusPayload | null;
  pagination: PaginationDto | null;
}

const initialState: TimetableState = {
  loadingTimetables: false,
  loadingTimetable: false,
  loadingTimetableUpdate: false,
  loadingTimetableDelete: false,

  errorLoadingTimetables: null,
  errorLoadingTimetable: null,
  errorUpdatingTimetable: null,
  errorDeletingTimetable: null,

  timetables: [],
  timetable: null,

  serverStatus: null,
  pagination: null,
};

export const timetableSlice = createSlice({
  name: "timetable",
  initialState,
  reducers: {
    setLoadingTimetables(state) {
      state.loadingTimetables = true;
      state.errorLoadingTimetables = null;
    },
    setTimetables(state, action: PayloadAction<TimetableSummaryDto[]>) {
      state.loadingTimetables = false;
      state.timetables = action.payload;
      state.errorLoadingTimetables = null;
    },
    setErrorLoadingTimetables(state, action: PayloadAction<string | null>) {
      state.loadingTimetables = false;
      state.errorLoadingTimetables = action.payload;
    },

    setLoadingTimetable(state) {
      state.loadingTimetable = true;
      state.errorLoadingTimetable = null;
      state.timetable = null;
    },
    setTimetable(state, action: PayloadAction<TimetableResponseDto>) {
      state.loadingTimetable = false;
      state.timetable = action.payload;
      state.errorLoadingTimetable = null;
    },
    setErrorLoadingTimetable(state, action: PayloadAction<string | null>) {
      state.loadingTimetable = false;
      state.errorLoadingTimetable = action.payload;
    },

    setLoadingTimetableUpdate(state) {
      state.loadingTimetableUpdate = true;
      state.errorUpdatingTimetable = null;
    },
    updateTimetableSuccess(state, action: PayloadAction<TimetableSummaryDto>) {
      state.loadingTimetableUpdate = false;
      state.errorUpdatingTimetable = null;

      state.timetables = state.timetables.map((t) =>
        t.id === action.payload.id
          ? { ...t, status: action.payload.status }
          : t,
      );
      if (state.timetable && state.timetable.id === action.payload.id) {
        state.timetable.status = action.payload.status;
      }
    },
    setErrorUpdatingTimetable(state, action: PayloadAction<string | null>) {
      state.loadingTimetableUpdate = false;
      state.errorUpdatingTimetable = action.payload;
    },

    setLoadingTimetableDelete(state) {
      state.loadingTimetableDelete = true;
      state.errorDeletingTimetable = null;
    },
    deleteTimetableSuccess(state, action: PayloadAction<string>) {
      state.loadingTimetableDelete = false;
      state.errorDeletingTimetable = null;
      state.timetables = state.timetables.filter(
        (t) => t.id !== action.payload,
      );
      if (state.timetable && state.timetable.id === action.payload) {
        state.timetable = null;
      }
    },
    setErrorDeletingTimetable(state, action: PayloadAction<string | null>) {
      state.loadingTimetableDelete = false;
      state.errorDeletingTimetable = action.payload;
    },

    setServerResponseStatus(
      state,
      action: PayloadAction<ServerStatusPayload | null>,
    ) {
      state.serverStatus = action.payload;
    },
    setPagination(state, action: PayloadAction<PaginationDto>) {
      state.pagination = action.payload;
    },
  },
});

export const timetableActions = timetableSlice.actions;
export default timetableSlice.reducer;
